import { reactive, ref, computed } from 'vue'
import { debounce } from 'lodash-es'

type InitTypeOption = {
  getApi: (params: any) => Promise<any>
  options?: any
  beforeGetData?: (params: any) => any
  afterGetData?: (data: any) => any
}
/**
 * 加载数据混入方法
 * @param {InitTypeOption} initData - 初始化配置
 * @param {function} initData.getApi - 数据请求 API 函数，需返回 Promise
 * @param {Object} [initData.options] - 初始化请求参数（可选）
 * @param {function} [initData.beforeGetData] - 请求前数据处理函数（可选）
 * @param {function} [initData.afterGetData] - 响应后数据处理函数（可选）
 * @returns {Object} 包含以下属性和方法：
 *  - searchParams: 请求参数对象（响应式）
 *  - dataList: 加载的数据列表（响应式引用）
 *  - total: 数据总条数（响应式引用）
 *  - loading: 加载状态（响应式引用）
 *  - noMore: 是否无更多数据（响应式引用）
 *  - getDataList: 执行数据请求的方法
 *  - reloadData: 重置参数并重新加载数据
 *  - loadMore: 加载更多数据（带防抖）
 */
export default function loadDataMixin(initData: InitTypeOption) {
  const { getApi, options, beforeGetData, afterGetData } = initData

  const loading = ref(false) // 加载状态
  const dataList = ref([]) // 数据列表
  const total = ref(0) // 数据总数
  const noMore = computed(() => dataList.value.length >= total.value) // 是否没有更多数据

  const initParams = () => {
    return {
      pageNum: 1,
      pageSize: 6,
      ...(options || {}),
    }
  }
  const searchParams = reactive(initParams())

  const getDataList = async () => {
    try {
      let params = {
        ...searchParams,
      }
      if (beforeGetData) params = beforeGetData(params)
      loading.value = true
      const { data } = await getApi(params)
      if (data) {
        let { list } = data
        if (afterGetData) list = afterGetData(data)
        total.value = parseInt(data.total, 10)
        dataList.value = list
        // 返回与写入 dataList 一致的（已映射）列表，供 loadMore 正确追加
        return list || []
      }
      return []
    } catch (error) {
      console.log(error)
      return null
    } finally {
      loading.value = false
    }
  }
  const reloadData = (params?: any) => {
    Object.assign(searchParams, initParams(), params || {})
    return getDataList()
  }
  const loadMore = debounce(async () => {
    if (noMore.value || loading.value) return
    searchParams.pageNum += 1
    const arr = [...dataList.value]
    const list = await getDataList()
    if (list) {
      dataList.value = [...arr, ...list]
    }
  }, 300)

  return {
    searchParams,
    dataList,
    total,
    loading,
    noMore,
    getDataList,
    reloadData,
    loadMore,
  }
}
